import React, { useMemo } from "react"
import { WingsSchoolCard } from "@wingspans-ui"
import { useIntl } from "react-intl"
import { JsonParam, useQueryParam } from "use-query-params"
import { useAllSchools } from "../../data/graphql"
import {
  localizeCostCheckboxTranslations,
  localizeSettingCheckboxTranslations,
  localizeSizeCheckboxTranslations,
  localizeTypeCheckboxTranslations,
} from "../algolia/localized-algolia-checkboxes"
import ExploreUnifiedFiltersDropdown from "./ExploreUnifiedFiltersDropdown"
import LocalExplore from "./LocalExplore"

const SCHOOLS_PER_PAGE = 9

const FILTER_KEYS = {
  size: "school_size_filter",
  setting: "school_setting_filter",
  type: "school_type_filter",
  selectivity: "school_selectivity_filter",
  cost: "school_cost_filter",
}
const FILTER_ID_SEPARATOR = "::"

const schoolsSearchKeys = [
  { name: "title", weight: 0.5 },
  { name: "school_keywords", weight: 0.3 },
  { name: "school_city", weight: 0.12 },
  { name: "school_state_or_region", weight: 0.08 },
]

const normalizeText = (value) => {
  return String(value || "").toLowerCase().trim()
}

const schoolMatchesAllQueryTokens = (school, query) => {
  const tokens = normalizeText(query)
    .split(/\s+/)
    .filter((token) => token.length >= 2)

  if (tokens.length <= 1) {
    return true
  }

  const searchableText = [
    school?.title,
    school?.school_keywords,
    school?.school_city,
    school?.school_state_or_region,
  ]
    .map(normalizeText)
    .join(" ")

  return tokens.every((token) => searchableText.includes(token))
}

const normalizeRefinementList = (refinementList) => {
  const normalized = {
    [FILTER_KEYS.size]: [],
    [FILTER_KEYS.setting]: [],
    [FILTER_KEYS.type]: [],
    [FILTER_KEYS.selectivity]: [],
    [FILTER_KEYS.cost]: [],
  }

  if (!refinementList || typeof refinementList !== "object") {
    return normalized
  }

  Object.keys(normalized).forEach((key) => {
    const values = refinementList[key]
    if (Array.isArray(values)) {
      normalized[key] = values
      return
    }

    normalized[key] = typeof values === "string" && values ? [values] : []
  })

  return normalized
}

const getLocalizedLabel = (value, translationMap, locale) => {
  return translationMap?.[value]?.[locale] || value
}

const matchesSelection = (value, selectedValues) => {
  if (!selectedValues.length) {
    return true
  }

  if (value === null || value === undefined) {
    return false
  }

  const itemValues = Array.isArray(value) ? value : [value]
  return itemValues.some((itemValue) => selectedValues.includes(itemValue))
}

const schoolMatchesRefinements = (school, refinementList) => {
  return matchesSelection(school[FILTER_KEYS.size], refinementList[FILTER_KEYS.size])
    && matchesSelection(school[FILTER_KEYS.setting], refinementList[FILTER_KEYS.setting])
    && matchesSelection(school[FILTER_KEYS.type], refinementList[FILTER_KEYS.type])
    && matchesSelection(school[FILTER_KEYS.selectivity], refinementList[FILTER_KEYS.selectivity])
    && matchesSelection(school[FILTER_KEYS.cost], refinementList[FILTER_KEYS.cost])
}

const mapSchoolToCard = (school) => {
  return (
    <WingsSchoolCard
      objectID={school.id}
      img={school.school_banner_image}
      title={school.title}
      slug={school.slug}
      contentType={school.type}
      city={school.school_city}
      state={school.school_state_or_region}
      acceptanceRate={school.school_acceptance_rate}
      enrolledCount={school.school_enrolled_count}
    />
  )
}

const SchoolsExplore = ({ classContext = "schools", filterList }) => {
  const { locale, formatMessage } = useIntl()
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const schools = useAllSchools(locale)

  const selectedRefinements = useMemo(() => {
    return normalizeRefinementList(queryFilters?.refinementList)
  }, [queryFilters])

  // Static filter options - no dataset iteration needed, instant rendering
  const sizeOptions = useMemo(() => {
    return [
      { value: "Small", label: getLocalizedLabel("Small", localizeSizeCheckboxTranslations, locale) },
      { value: "Medium", label: getLocalizedLabel("Medium", localizeSizeCheckboxTranslations, locale) },
      { value: "Large", label: getLocalizedLabel("Large", localizeSizeCheckboxTranslations, locale) },
    ]
  }, [locale])

  const settingOptions = useMemo(() => {
    return [
      { value: "Urban", label: getLocalizedLabel("Urban", localizeSettingCheckboxTranslations, locale) },
      { value: "Suburban", label: getLocalizedLabel("Suburban", localizeSettingCheckboxTranslations, locale) },
      { value: "Rural", label: getLocalizedLabel("Rural", localizeSettingCheckboxTranslations, locale) },
      { value: "Small town", label: getLocalizedLabel("Small town", localizeSettingCheckboxTranslations, locale) },
    ]
  }, [locale])

  const typeOptions = useMemo(() => {
    return [
      { value: "Public", label: getLocalizedLabel("Public", localizeTypeCheckboxTranslations, locale) },
      { value: "Private", label: getLocalizedLabel("Private", localizeTypeCheckboxTranslations, locale) },
    ]
  }, [locale])

  const selectivityOptions = useMemo(() => {
    const uniqueSelectivityValues = new Set()

    schools.forEach((school) => {
      const value = school[FILTER_KEYS.selectivity]
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((item) => uniqueSelectivityValues.add(item))
        return
      }

      if (value) {
        uniqueSelectivityValues.add(value)
      }
    })

    return Array.from(uniqueSelectivityValues)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((value) => ({
        value,
        label: value,
      }))
  }, [schools])

  const costOptions = useMemo(() => {
    return [
      { value: "Less Expensive", label: getLocalizedLabel("Less Expensive", localizeCostCheckboxTranslations, locale) },
      { value: "Somewhat Expensive", label: getLocalizedLabel("Somewhat Expensive", localizeCostCheckboxTranslations, locale) },
      { value: "Very Expensive", label: getLocalizedLabel("Very Expensive", localizeCostCheckboxTranslations, locale) },
    ]
  }, [locale])

  const filteredSchools = useMemo(() => {
    return schools.filter((school) => schoolMatchesRefinements(school, selectedRefinements))
  }, [schools, selectedRefinements])

  const toggleRefinement = (filterKey, value, isChecked) => {
    const currentRefinements = normalizeRefinementList(queryFilters?.refinementList)
    const currentValues = currentRefinements[filterKey] || []
    const nextValues = isChecked
      ? [...new Set([...currentValues, value])]
      : currentValues.filter((item) => item !== value)

    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {
        ...currentRefinements,
        [filterKey]: nextValues,
      },
    })
  }

  const clearRefinements = () => {
    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {},
    })
  }

  const selectedFilterOptionIds = useMemo(() => {
    return Object.entries(selectedRefinements).flatMap(([filterKey, values]) => {
      return (values || []).map((value) => `${filterKey}${FILTER_ID_SEPARATOR}${value}`)
    })
  }, [selectedRefinements])

  const filterSections = useMemo(() => {
    return [
      {
        id: FILTER_KEYS.size,
        title: formatMessage({ id: "school-explore-size", defaultMessage: "Size" }),
        columns: 3,
        options: sizeOptions.map((option) => ({
          id: `${FILTER_KEYS.size}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.setting,
        title: formatMessage({ id: "school-explore-setting", defaultMessage: "Setting" }),
        columns: 3,
        options: settingOptions.map((option) => ({
          id: `${FILTER_KEYS.setting}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.type,
        title: formatMessage({ id: "school-explore-type", defaultMessage: "Type" }),
        columns: 3,
        options: typeOptions.map((option) => ({
          id: `${FILTER_KEYS.type}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.selectivity,
        title: formatMessage({ id: "school-explore-acceptance-rate", defaultMessage: "Acceptance Rate" }),
        columns: 3,
        options: selectivityOptions.map((option) => ({
          id: `${FILTER_KEYS.selectivity}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.cost,
        title: formatMessage({ id: "school-explore-cost", defaultMessage: "Cost" }),
        columns: 3,
        options: costOptions.map((option) => ({
          id: `${FILTER_KEYS.cost}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
    ]
  }, [formatMessage, sizeOptions, settingOptions, typeOptions, selectivityOptions, costOptions])

  const handleUnifiedFilterToggle = (optionId) => {
    const [filterKey, value] = optionId.split(FILTER_ID_SEPARATOR)

    if (!filterKey || !value) {
      return
    }

    const isChecked = selectedRefinements[filterKey]?.includes(value)
    toggleRefinement(filterKey, value, !isChecked)
  }

  return (
    <LocalExplore
      classContext={classContext}
      filterList={filterList}
      items={filteredSchools}
      searchKeys={schoolsSearchKeys}
      hitsPerPage={SCHOOLS_PER_PAGE}
      searchPlaceholder={formatMessage({
        id: "algolia-schools-view-search-placeholder",
        defaultMessage: "Search by keyword, school name, city, state",
      })}
      preserveSearchRelevanceOrder={true}
      getRecencyDate={(item) => item.published_at || item.modified_at || item.created_at}
      maxSearchMatchScore={0.4}
      minQueryLengthForScoreFilter={4}
      fallbackToUnfilteredOnEmpty={true}
      filterItemByQuery={schoolMatchesAllQueryTokens}
      fallbackToUnfilteredOnPostFilterEmpty={true}
      renderCard={mapSchoolToCard}
      renderSortExtras={() => {
        return (
          <ExploreUnifiedFiltersDropdown
            heading={formatMessage({ id: "algolia-school-explore-filter-school-by", defaultMessage: "Filter Schools by" })}
            viewLabel={formatMessage({ id: "algolia-toggle-dropdown-schools", defaultMessage: "Schools" })}
            sections={filterSections}
            selectedOptionIds={selectedFilterOptionIds}
            onToggleOption={handleUnifiedFilterToggle}
            onClearAll={clearRefinements}
          />
        )
      }}
    />
  )
}

export default SchoolsExplore
